import React, { useMemo } from 'react';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { BodyM, BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import AlternativeSelect from 'components/AlternativeSelect/AlternativeSelect';
import CustomAvatar from 'components/CustomAvatar/CustomAvatar';
import CustomInput from 'components/CustomInput/CustomInput';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import Business from '@material-ui/icons/Business';
import styles from 'assets/jss/material-dashboard-pro-react/views/activeServices/addBillingAccountDialogStyle';
import { makeStyles } from '@material-ui/core/styles';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';

// @ts-ignore
const useStyles = makeStyles(styles);

type IAccountDetailsTabProps = {
	isEnterprise: boolean;
	billingAccountName: string;
	setBillingAccountName: (name: string) => void;
	billingAccountNumber: string;
	setBillingAccountNumber: (name: string) => void;
	selectedAccount: { id: string; name: string } | null;
	setSelectedAccount: (account: { id: string; name: string }) => void;
	accounts: Array<SingleCorpAccount>;
	loading: boolean;
	fetchingOrganizationData: boolean;
	showContactNumberError: boolean;
	showBillingAccountNumberError: boolean;
	showAccountExistsError: boolean;
	noNumbers: boolean;
	verificationNotSent: boolean;
};

const AccountDetailsTab = ({
	isEnterprise,
	billingAccountName,
	setBillingAccountName,
	billingAccountNumber,
	setBillingAccountNumber,
	selectedAccount,
	setSelectedAccount,
	accounts,
	loading,
	showContactNumberError,
	showBillingAccountNumberError,
	showAccountExistsError,
	noNumbers,
	verificationNotSent,
	fetchingOrganizationData,
}: IAccountDetailsTabProps) => {
	const classes = useStyles();
	const noNumberErrorMessage: string | undefined = showContactNumberError
		? translate('active.services.add.billing.account.no.contact.number')
		: undefined;
	const billingAccountErrorMessage: string | undefined = useMemo(() => {
		if (showAccountExistsError) {
			return translate('active.services.add.billing.account.account.exists');
		} else if (showBillingAccountNumberError) {
			return isEnterprise
				? translate('active.services.add.billing.account.incorrect.account.enterprise')
				: translate('active.services.add.billing.account.incorrect.account.corporate');
		} else if (noNumbers) {
			return translate('active.services.add.billing.account.no.numbers');
		}
		return undefined;
	}, [showBillingAccountNumberError, showAccountExistsError, noNumbers, isEnterprise]);
	const showBillingAccountError: boolean = showBillingAccountNumberError || noNumbers || showAccountExistsError;

	return (
		<div className={classes.tab}>
			<ConditionalRender
				show={isEnterprise && !loading}
				onTrue={() => (
					<div>
						<BodyS color="primary-light">
							{translate('active.services.add.billing.account.select.organization')}
						</BodyS>
						<AlternativeSelect
							value={
								<div>
									<ConditionalRender
										show={!!selectedAccount}
										onTrue={() => (
											<div className={classes.selectedItem}>
												<CustomAvatar
													accountId={selectedAccount?.id}
													alt="Company Logo"
													placeholderIcon={<Business />}
												/>
												<BodyS color="primary-light" disableMargin>
													{selectedAccount ? selectedAccount.name : translate('all.accounts')}
												</BodyS>
											</div>
										)}
										onFalse={() => (
											<BodyM color="secondary-light" disableMargin>
												{translate('organization.name')}
											</BodyM>
										)}
									/>
								</div>
							}
							onClick={(item: any) => setSelectedAccount(item)}
							items={accounts.map((account) => ({
								id: account.accountId,
								name: account.accountName,
							}))}
							selectedItems={selectedAccount ? [selectedAccount.id] : []}
							error={noNumberErrorMessage}
							showError={showBillingAccountNumberError}
						/>
						<ConditionalRender
							show={showContactNumberError}
							onTrue={() => (
								<BodyS color="danger">
									{translate('active.services.add.billing.account.please.contact.omantel')}
								</BodyS>
							)}
						/>
					</div>
				)}
			/>
			<ConditionalRender
				show={!loading && !fetchingOrganizationData}
				onTrue={() => (
					<>
						<ConditionalRender
							show={!!selectedAccount && !showContactNumberError}
							onTrue={() => (
								<>
									<CustomInput
										labelText={translate('active.services.billing.account.number')}
										formControlProps={{ fullWidth: true }}
										inputProps={{
											value: billingAccountNumber,
											onChange: ({ target: { value } }: { target: { value: string } }) =>
												setBillingAccountNumber(value),
										}}
										error={showBillingAccountError}
										helperText={billingAccountErrorMessage}
									/>
									<CustomInput
										labelText={translate('active.services.billing.account.name')}
										formControlProps={{ fullWidth: true }}
										inputProps={{
											value: billingAccountName,
											onChange: ({ target: { value } }: { target: { value: string } }) =>
												setBillingAccountName(value),
										}}
										helperText={translate(
											'active.services.add.billing.account.name.your.billing.account'
										)}
									/>
								</>
							)}
						/>
						<ConditionalRender
							show={verificationNotSent}
							onTrue={() => (
								<BodyS color="danger">
									{translate('active.services.add.billing.account.verification.not.sent')}
								</BodyS>
							)}
						/>
					</>
				)}
				onFalse={() => <CustomCircularProgress text={translate('loading')} loading transparent />}
			/>
		</div>
	);
};

export default AccountDetailsTab;
